import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Container from 'common/Container';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import { WikiHero, WikiManualSection } from '../WikiOperadorAudiovisual/components';
import { fetchKnowledgePagesByCategory } from 'services/academyKnowledgeApi';
import {
  WIKI_PAUTAS_EXTERIORES_CATEGORY,
  WIKI_PAUTAS_EXTERIORES_HERO_SLUG,
} from 'constants/wikiPautasExteriores';

function sortByOrder(pages) {
  return [...pages].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

const LandingWikiPautasExteriores = ({ themeMode = 'light' }) => {
  const theme = useTheme();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchKnowledgePagesByCategory(WIKI_PAUTAS_EXTERIORES_CATEGORY);
        if (!cancelled) setPages(Array.isArray(data) ? sortByOrder(data) : []);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'No se pudo cargar el manual.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const { hero, sections } = useMemo(() => {
    const sorted = sortByOrder(pages);
    const heroIdx = sorted.findIndex((p) => p.slug === WIKI_PAUTAS_EXTERIORES_HERO_SLUG);
    const heroPage = heroIdx >= 0 ? sorted[heroIdx] : sorted[0] || null;
    const rest =
      heroIdx >= 0
        ? sorted.filter((_, i) => i !== heroIdx)
        : sorted.length > 1
          ? sorted.slice(1)
          : [];
    return { hero: heroPage, sections: rest };
  }, [pages]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (error) {
    return (
      <Container>
        <Box py={8}>
          <Typography variant="h5" gutterBottom>
            No se pudo cargar el manual
          </Typography>
          <Typography color="textSecondary" paragraph>
            {error}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Categoría Academy: «{WIKI_PAUTAS_EXTERIORES_CATEGORY}». Poblar con{' '}
            <code>npm run seed:wiki-pautas-exteriores</code> en <code>tnoradio-academy-service</code>.
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!hero) {
    return (
      <Container>
        <Box py={8}>
          <Typography variant="h5" gutterBottom>
            Manual aún sin publicar
          </Typography>
          <Typography color="textSecondary">
            No hay páginas publicadas para este manual. Ejecuta el seed en Academy o crea entradas en la categoría
            indicada.
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Box>
      <Box minHeight={{ xs: 'auto', md: 'calc(100vh - 64px)' }} position="relative">
        <WikiHero
          overline="Pautas en locación"
          title={hero.title}
          description={hero.description || ''}
          contentHtml={hero.content || ''}
        />
      </Box>
      {sections.map((page, index) => {
        const variant = index % 3 === 0 ? 'highlight' : 'plain';
        const block = (
          <WikiManualSection
            key={page.slug || page._id}
            title={page.title}
            contentHtml={page.content || ''}
            variant={variant}
          />
        );
        if (index % 2 === 1) {
          return (
            <Box key={page.slug || page._id} bgcolor={theme.palette.alternate.main}>
              <Container>{block}</Container>
            </Box>
          );
        }
        return (
          <Container key={page.slug || page._id}>
            {block}
          </Container>
        );
      })}
    </Box>
  );
};

LandingWikiPautasExteriores.propTypes = {
  themeMode: PropTypes.string,
};

export default LandingWikiPautasExteriores;
